import SearchIcon from "@mui/icons-material/Search";
import { Box, InputAdornment, TextField, Typography } from "@mui/material";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import useThemeStore from "@/shared/stores/useThemeStore";
import GlobalNoData from "@/shared/components/GlobalNoData";

import { DeckCardType, PublishedCard } from "@/cards/common/types";
import {
  injectTalentExpCreationPage,
  SINGLE_CARD_NAVIGATION,
  type OwnedDeckCardNavigationConfig,
  type OwnedDeckCardPurchaseFilter,
} from "@/cards/common/utils/app-deck/ownedDeckCardList.utils";
import {
  useOwnedDeckCardList,
  type OwnedDeckCardTitleFormat,
  type UseOwnedDeckCardListOptions,
} from "./useOwnedDeckCardList";
import { DeckCardThumbnailRecord } from "@/cards/common/types/app-deck/deckCardThumbnail.types";
import PublishedCardPreviewModal from "../../preview-modal/PublishedCardPreviewModal";
import AllTalentFilterOption from "../AllTalentFilterOption";
import DeckCardGrid from "../DeckCardGrid";
import { DeckCardKakaoKind } from "@/modules/kakao";
// import type { DeckCardThumbnailRecord } from "../deckCardThumbnail.types";

export type OwnedDeckCardListPreviewContext = {
  selectedCard: PublishedCard | null;
  isPreviewOpen: boolean;
  handlePreviewClose: () => void;
  /** Refetch GET /main-card/:id for the open preview (e.g. after talent adds a page). */
  refetchSelectedCard?: () => void;
};

export type OwnedDeckCardListProps = {
  deckCardType: DeckCardType;
  viewMode: "user" | "talent";
  purchaseFilter?: OwnedDeckCardPurchaseFilter;
  navigation?: OwnedDeckCardNavigationConfig;
  tabParamKey?: string;
  tabParamValue?: string;
  thumbnailExtraParams?: Record<string, string>;
  titleLabel?: string;
  titleFormat?: OwnedDeckCardTitleFormat;
  showTalentFilter?: boolean;
  emptyTitle?: string;
  emptySubtitle?: string;
  emptyNs?: string;
  /** Talent book-view: inject deck-specific exp creation page before preview. */
  injectTalentCreationPage?: boolean;
  appendEndingCoverOnFetch?: boolean;
  enabled?: boolean;
  mapFetchedMainCard?: UseOwnedDeckCardListOptions["mapFetchedMainCard"];
  enrichThumbnailCard?: (card: PublishedCard, record: DeckCardThumbnailRecord) => PublishedCard;
  secondaryUrlParam?: UseOwnedDeckCardListOptions["secondaryUrlParam"];
  extraUrlParamsToClear?: string[];
  resolveDeepLinkCard?: UseOwnedDeckCardListOptions["resolveDeepLinkCard"];
  filterVisibleCard?: UseOwnedDeckCardListOptions["filterVisibleCard"];
  onAfterPreviewClose?: () => void;
  refetchOnPreviewClose?: boolean;
  onExpandShare?: (card: PublishedCard) => void;
  deckCardShareKind?: DeckCardKakaoKind;
  renderPreviewModal?: (ctx: OwnedDeckCardListPreviewContext) => React.ReactNode;
};

const OwnedDeckCardList: React.FC<OwnedDeckCardListProps> = ({
  deckCardType,
  viewMode,
  purchaseFilter = viewMode === "user" ? "purchased" : "all",
  navigation = SINGLE_CARD_NAVIGATION,
  tabParamKey,
  tabParamValue,
  thumbnailExtraParams,
  titleLabel,
  showTalentFilter = viewMode === "user",
  emptyTitle,
  emptySubtitle,
  emptyNs = "taascard",
  injectTalentCreationPage = viewMode === "talent",
  appendEndingCoverOnFetch = true,
  enabled = true,
  titleFormat = "possessive",
  mapFetchedMainCard,
  enrichThumbnailCard,
  secondaryUrlParam,
  extraUrlParamsToClear,
  resolveDeepLinkCard,
  filterVisibleCard,
  onAfterPreviewClose,
  refetchOnPreviewClose = false,
  onExpandShare,
  deckCardShareKind,
  renderPreviewModal,
}) => {
  const { darkMode } = useThemeStore();
  const { t } = useTranslation();

  const {
    loading,
    filteredCards,
    uniqueTalents,
    thumbnailsByCardId,
    searchQuery,
    setSearchQuery,
    selectedTalentId,
    setSelectedTalentId,
    selectedCard,
    isPreviewOpen,
    handleCardClick,
    handlePreviewClose,
    expandPortalProps,
    refetchSelectedCard,
  } = useOwnedDeckCardList({
    deckCardType,
    viewMode,
    purchaseFilter,
    navigation,
    tabParamKey,
    tabParamValue,
    thumbnailExtraParams,
    titleLabel,
    appendEndingCoverOnFetch,
    enabled,
    titleFormat,
    mapFetchedMainCard,
    enrichThumbnailCard,
    secondaryUrlParam,
    extraUrlParamsToClear,
    resolveDeepLinkCard,
    filterVisibleCard,
    onAfterPreviewClose,
    refetchOnPreviewClose,
    onExpandShare,
    deckCardShareKind,
  });

  const previewCard = useMemo(() => {
    if (!selectedCard) return null;
    if (injectTalentCreationPage) {
      return injectTalentExpCreationPage(selectedCard, deckCardType);
    }
    return selectedCard;
  }, [selectedCard, injectTalentCreationPage, deckCardType]);

  return (
    <Box sx={{ minHeight: "40vh", position: "relative" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: { xs: 1.5, sm: 2 },
          mb: { xs: 2, sm: 3 },
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontFamily: '"Lexend", sans-serif',
            fontWeight: 700,
            fontSize: { xs: "1rem", sm: "1.125rem" },
            color: darkMode ? "var(--neutralWhite)" : "var(--neutral-900)",
          }}
        >
          {loading
            ? t("loading_ellipsis", "Loading…")
            : `${filteredCards.length} ${
                filteredCards.length === 1 ? t("card", "card") : t("cards", "cards")
              }`}
        </Typography>

        <TextField
          placeholder={t("search_cards", "Search cards")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          sx={{
            width: { xs: "100%", sm: 280, md: 320 },
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              bgcolor: darkMode ? "var(--neutral-800)" : "var(--neutralWhite)",
              border: darkMode ? "1px solid var(--neutral-700)" : "1px solid var(--neutral-200)",
              "& fieldset": { border: "none" },
              "&:hover": {
                border: darkMode ? "1px solid var(--neutral-600)" : "1px solid var(--neutral-300)",
              },
              "&.Mui-focused": {
                border: darkMode ? "1px solid var(--primary-500)" : "1px solid var(--primary-600)",
              },
            },
            "& .MuiInputBase-input": {
              color: darkMode ? "var(--neutralWhite)" : "var(--neutral-900)",
              fontSize: "0.875rem",
              py: 1.25,
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon
                  sx={{
                    color: darkMode ? "var(--neutral-400)" : "var(--neutral-500)",
                    fontSize: "1.125rem",
                  }}
                />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {!loading && showTalentFilter && uniqueTalents.length > 0 && (
        <Box sx={{ mb: 3, display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "nowrap",
              gap: { xs: 1.5, sm: 2 },
              p: 2,
              pl: 1,
              alignItems: "flex-start",
              overflowX: "auto",
              overflowY: "hidden",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <AllTalentFilterOption
              selected={selectedTalentId === null}
              darkMode={darkMode}
              cardType={deckCardType}
              onClick={() => setSelectedTalentId(null)}
            />
            {uniqueTalents.map((talent) => (
              <Box
                key={talent.id}
                onClick={() =>
                  setSelectedTalentId(selectedTalentId === talent.id ? null : talent.id)
                }
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  cursor: "pointer",
                  gap: 0.75,
                  opacity: selectedTalentId === talent.id || selectedTalentId === null ? 1 : 0.6,
                  transition: "opacity 0.2s",
                  "&:hover": { opacity: 1 },
                }}
              >
                <Box
                  sx={{
                    width: { xs: 64, sm: 74 },
                    height: { xs: 64, sm: 74 },
                    borderRadius: "50%",
                    border: `2px solid ${
                      selectedTalentId === talent.id
                        ? darkMode
                          ? "var(--primary-400)"
                          : "var(--primary-600)"
                        : darkMode
                          ? "var(--neutral-700)"
                          : "var(--neutral-300)"
                    }`,
                    overflow: "hidden",
                    backgroundColor: darkMode ? "var(--neutral-800)" : "var(--neutral-100)",
                    transition: "all 0.2s",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {talent.profile_image ? (
                    <Box
                      component="img"
                      src={talent.profile_image}
                      alt={talent.username}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <Typography
                      sx={{
                        fontSize: { xs: "1.25rem", sm: "1.5rem" },
                        fontWeight: 600,
                        color: darkMode ? "var(--neutral-300)" : "var(--neutral-700)",
                      }}
                    >
                      {talent.username.charAt(0).toUpperCase()}
                    </Typography>
                  )}
                </Box>
                <Typography
                  sx={{
                    fontSize: { xs: "0.6875rem", sm: "0.75rem" },
                    color: darkMode ? "var(--neutral-400)" : "var(--neutral-600)",
                    textAlign: "center",
                    maxWidth: { xs: 56, sm: 64 },
                    textWrap: "wrap",
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                  }}
                >
                  {talent.username}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {viewMode === "talent" && (
        <Box
          sx={{
            borderBottom: 1,
            borderColor: darkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
            mb: { xs: 2, sm: 3 },
            overflowX: "auto",
          }}
        />
      )}

      {!loading && filteredCards.length === 0 ? (
        <GlobalNoData
          title={emptyTitle}
          subtitle={emptySubtitle}
          titleKey={emptyTitle}
          subtitleKey={emptySubtitle}
          ns={emptyNs}
        />
      ) : (
        <DeckCardGrid
          loading={loading}
          cards={filteredCards}
          cardType={deckCardType}
          darkMode={darkMode}
          searchQuery={searchQuery}
          onCardClick={handleCardClick}
          thumbnailsByCardId={thumbnailsByCardId}
          expandPortalProps={expandPortalProps}
        />
      )}

      {renderPreviewModal ? (
        renderPreviewModal({
          selectedCard,
          isPreviewOpen,
          handlePreviewClose,
          refetchSelectedCard,
        })
      ) : (
        <PublishedCardPreviewModal
          open={isPreviewOpen}
          onClose={handlePreviewClose}
          card={previewCard}
        />
      )}
    </Box>
  );
};

export default OwnedDeckCardList;
